import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { User, Palette, Eye, EyeOff, CheckCircle, AlertCircle, Loader2, ExternalLink, Save, RefreshCw, WifiOff, AlertTriangle, Settings as SettingsIcon, Crown, Users } from 'lucide-react';
import { HistoricalImport } from '../components/budget/HistoricalImport';
import { useAuth } from '../hooks/useAuth';
import { useUserProfile } from '../hooks/useUserProfile'; 
import { useTheme } from '../hooks/useTheme';
import { usePermissions } from '../hooks/usePermissions';
import { useProxy } from '../contexts/ProxyContext';
import { useEffectivePermissions } from '../hooks/useEffectivePermissions';
import { useIntegrationStatus } from '../hooks/useIntegrationStatus';
import { integrationSettings } from '../../backend/services/integrationSettings';
import { supabase } from '../../backend/services/supabase';

// Helper component for form fields with validation - moved outside to prevent re-creation
const FormField: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: 'text' | 'password' | 'email';
  placeholder?: string;
  validate?: (value: string) => string | undefined;
  required?: boolean;
  showToggle?: boolean;
  showPassword?: boolean;
  onTogglePassword?: () => void;
}> = ({ 
  label, 
  value, 
  onChange, 
  type = 'text', 
  placeholder, 
  validate, 
  required = false,
  showToggle = false,
  showPassword = false,
  onTogglePassword
}) => {
  const error = validate ? validate(value) : undefined;
  const showError = value.length > 0 && error;

  return (
    <div className="space-y-1">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative">
        <input
          type={showToggle ? (showPassword ? 'text' : 'password') : type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
            showError 
              ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
              : 'border-gray-300 dark:border-gray-600 focus:border-blue-500'
          }`}
        />
        {showToggle && onTogglePassword && (
          <button
            type="button"
            className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            onClick={onTogglePassword}
          >
            {showPassword ? (
              <EyeOff className="h-4 w-4" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
          </button>
        )}
      </div>
      {showError && (
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      )}
    </div>
  );
};

export const Settings: React.FC = () => {
  const location = useLocation();
  const { user } = useAuth();
  const { profile, loading: profileLoading, updateProfile } = useUserProfile();
  const { theme, setTheme } = useTheme();
  const { isAdmin, hasRole, roles } = usePermissions();
  const { proxyRole, setProxyRole, isProxyActive, effectiveRole, availableRoles } = useProxy();
  const { hasRole: effectiveHasRole, roles: effectiveRoles } = useEffectivePermissions();
  
  // Form states
  const [firstName, setFirstName] = useState(profile?.first_name || '');
  const [lastName, setLastName] = useState(profile?.last_name || '');
  
  // API credentials state
  const [spotifyClientId, setSpotifyClientId] = useState('');
  const [spotifyClientSecret, setSpotifyClientSecret] = useState('');
  const [metaAppId, setMetaAppId] = useState('');
  const [metaAppSecret, setMetaAppSecret] = useState('');
  const [metaAccessToken, setMetaAccessToken] = useState('');
  const [metaAccountId, setMetaAccountId] = useState('');
  // OpenAI
  const [openaiApiKey, setOpenaiApiKey] = useState('');
  const [openaiModel, setOpenaiModel] = useState('gpt-4o-mini');
  
  // Integration status states
  const [spotifyStatus, setSpotifyStatus] = useState<'unconfigured' | 'valid' | 'invalid' | 'testing'>('unconfigured');
  const [metaStatus, setMetaStatus] = useState<'unconfigured' | 'valid' | 'invalid' | 'testing'>('unconfigured');
  const [lastSpotifyTest, setLastSpotifyTest] = useState<Date | null>(null);
  const [lastMetaTest, setLastMetaTest] = useState<Date | null>(null);
  const [spotifyTestError, setSpotifyTestError] = useState<string | null>(null);
  const [metaTestError, setMetaTestError] = useState<string | null>(null);
  
  // Use persistent integration status
  const { getStatusForProvider, refreshStatus } = useIntegrationStatus();
  const [openaiStatus, setOpenaiStatus] = useState<'unconfigured' | 'valid' | 'invalid' | 'testing'>('unconfigured');
  
  // Helper function to save credential status
  const saveCredentialStatus = async (provider: string, status: string, errorMessage: string | null, details: any) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;

      await fetch('/api/save-credential-status', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          provider,
          status,
          errorMessage,
          details
        })
      });
    } catch (error) {
      console.error(`Error saving ${provider} status:`, error);
    }
  };
  
  // Fetch OpenAI models when API key changes
  useEffect(() => {
    if (openaiApiKey.trim() && openaiApiKey.startsWith('sk-')) {
      fetchOpenAIModels(openaiApiKey);
    }
  }, [openaiApiKey]);

  // Sync persistent status with local state
  useEffect(() => {
    // Meta status
    const metaStatusData = getStatusForProvider('meta');
    if (metaStatusData) {
      if (metaStatusData.status === 'valid') {
        setMetaStatus('valid');
        setMetaTestError(`✅ Connected to: ${metaStatusData.validation_details?.account_name || 'Meta Ads'} (${metaStatusData.validation_details?.campaigns_accessible || 0} campaigns accessible)`);
        setLastMetaTest(metaStatusData.last_success_at ? new Date(metaStatusData.last_success_at) : null);
      } else if (metaStatusData.status === 'invalid') {
        setMetaStatus('invalid');
        setMetaTestError(metaStatusData.error_message || 'Invalid credentials');
        setLastMetaTest(metaStatusData.last_error_at ? new Date(metaStatusData.last_error_at) : null);
      } else if (metaStatusData.status === 'testing') {
        setMetaStatus('testing');
        setMetaTestError(null);
      } else {
        setMetaStatus('unconfigured');
        setMetaTestError(null);
      }
    } else {
      setMetaStatus('unconfigured');
      setMetaTestError(null);
    }

    // Spotify status
    const spotifyStatusData = getStatusForProvider('spotify');
    if (spotifyStatusData) {
      if (spotifyStatusData.status === 'valid') {
        setSpotifyStatus('valid');
        setSpotifyTestError(spotifyStatusData.validation_details?.message || '✅ Connected to Spotify API successfully');
        setLastSpotifyTest(spotifyStatusData.last_success_at ? new Date(spotifyStatusData.last_success_at) : null);
      } else if (spotifyStatusData.status === 'invalid') {
        setSpotifyStatus('invalid');
        setSpotifyTestError(spotifyStatusData.error_message || 'Invalid credentials');
        setLastSpotifyTest(spotifyStatusData.last_error_at ? new Date(spotifyStatusData.last_error_at) : null);
      } else if (spotifyStatusData.status === 'testing') {
        setSpotifyStatus('testing');
        setSpotifyTestError(null);
      } else {
        setSpotifyStatus('unconfigured');
        setSpotifyTestError(null);
      }
    } else {
      setSpotifyStatus('unconfigured');
      setSpotifyTestError(null);
    }

    // OpenAI status
    const openaiStatusData = getStatusForProvider('openai');
    if (openaiStatusData) {
      if (openaiStatusData.status === 'valid') {
        setOpenaiStatus('valid');
        setOpenaiTestError(openaiStatusData.validation_details?.message || '✅ OpenAI API key format is valid');
        setLastOpenaiTest(openaiStatusData.last_success_at ? new Date(openaiStatusData.last_success_at) : null);
      } else if (openaiStatusData.status === 'invalid') {
        setOpenaiStatus('invalid');
        setOpenaiTestError(openaiStatusData.error_message || 'Invalid API key');
        setLastOpenaiTest(openaiStatusData.last_error_at ? new Date(openaiStatusData.last_error_at) : null);
      } else if (openaiStatusData.status === 'testing') {
        setOpenaiStatus('testing');
        setOpenaiTestError(null);
      } else {
        setOpenaiStatus('unconfigured');
        setOpenaiTestError(null);
      }
    } else {
      setOpenaiStatus('unconfigured');
      setOpenaiTestError(null);
    }
  }, [getStatusForProvider]);
  const [lastOpenaiTest, setLastOpenaiTest] = useState<Date | null>(null);
  const [openaiTestError, setOpenaiTestError] = useState<string | null>(null);
  
  // State for showing/hiding masked fields
  const [showMetaSecret, setShowMetaSecret] = useState(false);
  const [showSpotifySecret, setShowSpotifySecret] = useState(false);
  const [showOpenaiKey, setShowOpenaiKey] = useState(false);
  
  // OpenAI models state
  const [openaiModels, setOpenaiModels] = useState([
    { value: 'gpt-4o-mini', label: 'GPT-4o Mini (Enter API key to load models)' }
  ]);
  const [loadingModels, setLoadingModels] = useState(false);
  
  // Function to fetch available OpenAI models
  const fetchOpenAIModels = async (apiKey: string) => {
    if (!apiKey.trim()) return;
    
    setLoadingModels(true);
    try {
      const response = await fetch(`/api/openai/models?api_key=${encodeURIComponent(apiKey)}`);
      const result = await response.json();
      
      if (response.ok && Array.isArray(result.models)) {
        setOpenaiModels(result.models);
        console.log('✅ Loaded OpenAI models:', result.models);
      } else {
        console.warn('⚠️ Failed to load models:', result.error);
        // No fallback list with invalid key to avoid confusion
        setOpenaiModels([]);
      }
    } catch (error) {
      console.error('❌ Error fetching models:', error);
      // No fallback list with invalid key
      setOpenaiModels([]);
    } finally {
      setLoadingModels(false);
    }
  };
  
  // UI states
  const [activeTab, setActiveTab] = useState('profile');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [helpModal, setHelpModal] = useState<{ open: boolean; provider?: 'spotify' | 'meta' | 'openai' }>({ open: false });
  

  // Form validation functions - only validate on save, not on every keystroke
  const validateName = (name: string): string | undefined => {
    if (!name.trim()) return 'Name is required';
    if (name.trim().length > 50) return 'Name must be less than 50 characters';
    return undefined;
  };


  // Form handlers
  const handleProfileUpdate = async () => {
    const firstNameError = validateName(firstName);
    const lastNameError = validateName(lastName);
    
    if (firstNameError || lastNameError) {
      setMessage({ type: 'error', text: 'Please fix the validation errors before saving' });
      return;
    }

    setSaving(true);
    setMessage(null);

    try {
      const result = await updateProfile({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
      });
      
      if (result.error) {
        setMessage({ type: 'error', text: result.error });
      } else {
        setMessage({ type: 'success', text: 'Profile updated successfully!' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update profile' });
    } finally {
      setSaving(false);
    }
  };


  // Update form state when profile loads
  useEffect(() => {
    if (profile) {
      setFirstName(profile.first_name || '');
      setLastName(profile.last_name || '');
    }
  }, [profile]);


  // Load saved credentials on mount
  useEffect(() => {
    // Local fallback (legacy)
    const savedSpotifyId = localStorage.getItem('spotify_client_id');
    const savedSpotifySecret = localStorage.getItem('spotify_client_secret');
    const savedMetaId = localStorage.getItem('meta_app_id');
    const savedMetaSecret = localStorage.getItem('meta_app_secret');
    const savedOpenaiKey = localStorage.getItem('openai_api_key');
    const savedOpenaiModel = localStorage.getItem('openai_default_model');

    if (savedSpotifyId) setSpotifyClientId(savedSpotifyId);
    if (savedSpotifySecret) setSpotifyClientSecret(savedSpotifySecret);
    if (savedMetaId) setMetaAppId(savedMetaId);
    if (savedMetaSecret) setMetaAppSecret(savedMetaSecret);
    if (savedOpenaiKey) setOpenaiApiKey(savedOpenaiKey);
    if (savedOpenaiModel) setOpenaiModel(savedOpenaiModel);

    // Supabase-backed settings
    (async () => {
      const [openai, spotify, meta] = await Promise.all([
        integrationSettings.get('openai'),
        integrationSettings.get('spotify'),
        integrationSettings.get('meta'),
      ]);
      if (openai) {
        if (openai.openai_api_key) setOpenaiApiKey(openai.openai_api_key);
        if (openai.openai_model) setOpenaiModel(openai.openai_model);
      }
      if (spotify) {
        if (spotify.spotify_client_id) setSpotifyClientId(spotify.spotify_client_id);
        if (spotify.spotify_client_secret) setSpotifyClientSecret(spotify.spotify_client_secret);
      }
      if (meta) {
        if (meta.meta_app_id || meta.app_id) setMetaAppId(meta.meta_app_id || meta.app_id);
        if (meta.meta_app_secret || meta.app_secret) setMetaAppSecret(meta.meta_app_secret || meta.app_secret);
        if (meta.meta_access_token || meta.access_token) setMetaAccessToken(meta.meta_access_token || meta.access_token);
        if (meta.meta_account_id || meta.account_id) setMetaAccountId((meta.meta_account_id || meta.account_id) + '');
      }
    })();
  }, []);

  // Spotify API test and save handlers
  const handleSpotifyTest = async () => {
    setSpotifyStatus('testing');
    setSpotifyTestError(null);

    try {
      if (!spotifyClientId.trim() || !spotifyClientSecret.trim()) {
        setSpotifyStatus('invalid');
        setSpotifyTestError('Client ID and Secret are required');
        await saveCredentialStatus('spotify', 'invalid', 'Client ID and Secret are required', {});
        return;
      }

      // First save the credentials to test them
      const ok = await integrationSettings.set('spotify', {
        spotify_client_id: spotifyClientId,
        spotify_client_secret: spotifyClientSecret,
      });
      
      if (!ok) {
        setSpotifyStatus('invalid');
        setSpotifyTestError('Failed to save credentials for testing');
        await saveCredentialStatus('spotify', 'invalid', 'Failed to save credentials for testing', {});
        return;
      }

      // Test the credentials directly with Spotify API
      const basic = btoa(`${spotifyClientId}:${spotifyClientSecret}`);
      const tokenResp = await fetch('https://accounts.spotify.com/api/token', {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${basic}`,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: 'grant_type=client_credentials'
      });

      if (!tokenResp.ok) {
        const errorData = await tokenResp.json().catch(() => ({}));
        setSpotifyStatus('invalid');
        setSpotifyTestError(errorData.error_description || 'Spotify authentication failed');
        await saveCredentialStatus('spotify', 'invalid', errorData.error_description || 'Spotify authentication failed', {
          error: errorData.error,
          status: tokenResp.status
        });
        return;
      }

      const tokenData = await tokenResp.json();
      
      setSpotifyStatus('valid');
      setLastSpotifyTest(new Date());
      setSpotifyTestError('✅ Connected to Spotify API successfully');
      await saveCredentialStatus('spotify', 'valid', null, { 
        message: 'Connected to Spotify API successfully',
        token_type: tokenData.token_type,
        expires_in: tokenData.expires_in
      });

    } catch (error) {
      setSpotifyStatus('invalid');
      setSpotifyTestError(error instanceof Error ? error.message : 'Connection failed');
      await saveCredentialStatus('spotify', 'invalid', error instanceof Error ? error.message : 'Connection failed', {});
    }
  };

  const handleSpotifySave = async () => {
    setSaving(true);
    setMessage(null);

    try {
      // Persist via Supabase (falls back to localStorage if unauth)
      const ok = await integrationSettings.set('spotify', {
        spotify_client_id: spotifyClientId,
        spotify_client_secret: spotifyClientSecret,
      });
      if (!ok) throw new Error('Failed to persist settings');
      
      // Test the credentials after saving
      await handleSpotifyTest();
      
      setMessage({ type: 'success', text: 'Spotify credentials saved successfully!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save Spotify credentials' });
    } finally {
      setSaving(false);
    }
  };

  // Meta API test and save handlers
  const handleMetaTest = async (): Promise<any | null> => {
    setMetaStatus('testing');
    setMetaTestError(null);
    
    try {
      if (!metaAccessToken || !metaAccountId) {
        setMetaStatus('invalid');
        setMetaTestError('Access Token and Ad Account ID are required');
        return null;
      }

      const response = await fetch('/api/meta/test-credentials', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          access_token: metaAccessToken,
          app_id: metaAppId || undefined,
          app_secret: metaAppSecret || undefined,
          account_id: metaAccountId
        })
      });

      const parseJsonSafe = async (r: Response) => {
        const text = await r.text();
        try { return JSON.parse(text || '{}'); } catch { return { raw: text }; }
      };
      const result = await parseJsonSafe(response as any);
      
      if (!response.ok) {
      setMetaStatus('invalid');
      setMetaTestError(result?.hint || result?.details || result?.error || 'Test failed');
      
      // Refresh the persistent status
      await refreshStatus('meta');
      
      return null;
      }

      setMetaStatus('valid');
      setLastMetaTest(new Date());
      setMetaTestError(`✅ Connected to: ${result.account.name} (${result.sample_campaigns} campaigns accessible)`);
      
      // Refresh the persistent status
      await refreshStatus('meta');
      
      return result;
    } catch (e) {
      setMetaStatus('invalid');
      setMetaTestError(e instanceof Error ? e.message : 'Network error - check your connection');
      return null;
    }
  };

  const handleMetaSave = async () => {
    setSaving(true);
    setMessage(null);

    try {
      // Quick test first; if valid, also persist returned account meta
      const verification = await handleMetaTest();
      const ok = await integrationSettings.set('meta', {
        access_token: metaAccessToken,
        app_id: metaAppId,
        app_secret: metaAppSecret,
        account_id: metaAccountId,
        // Duplicate keys for backward/forward compatibility with existing readers
        meta_access_token: metaAccessToken,
        meta_app_id: metaAppId,
        meta_app_secret: metaAppSecret,
        meta_account_id: metaAccountId,
        // Store account metadata for UX (name/currency/timezone)
        account_meta: verification?.account || null,
        last_verified_at: new Date().toISOString()
      });
      if (!ok) throw new Error('Failed to persist settings');
      
      setMessage({ type: 'success', text: 'Meta credentials saved successfully!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save Meta credentials' });
    } finally {
      setSaving(false);
    }
  };

  // OpenAI test and save handlers
  const handleOpenAITest = async () => {
    setOpenaiStatus('testing');
    setOpenaiTestError(null);
    
    try {
      if (!openaiApiKey.trim()) {
        setOpenaiStatus('invalid');
        setOpenaiTestError('API key is required');
        await saveCredentialStatus('openai', 'invalid', 'API key is required', {});
        return;
      }

      // Test the API key with a real API call
      const response = await fetch('/api/openai/test-credentials', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          api_key: openaiApiKey,
          model: openaiModel
        })
      });

      const result = await response.json();
      console.log('OpenAI test result:', result);

      if (response.ok) {
        setOpenaiStatus('valid');
        setLastOpenaiTest(new Date());
        setOpenaiTestError(`✅ OpenAI API key is valid (${openaiModel})`);
        await saveCredentialStatus('openai', 'valid', null, { 
          message: `OpenAI API key is valid (${openaiModel})`,
          model: openaiModel
        });
      } else {
        setOpenaiStatus('invalid');
        // Try to get a user-friendly error message
        let errorMessage = 'API key validation failed';
        if (result?.hint) {
          errorMessage = result.hint;
        } else if (result?.error) {
          errorMessage = result.error;
        } else if (result?.details?.error?.message) {
          errorMessage = result.details.error.message;
        }
        
        setOpenaiTestError(errorMessage);
        await saveCredentialStatus('openai', 'invalid', errorMessage, {
          error: result?.error,
          hint: result?.hint,
          status: result?.status,
          details: result?.details
        });
      }
    } catch (error) {
      setOpenaiStatus('invalid');
      setOpenaiTestError(error instanceof Error ? error.message : 'Connection failed');
      await saveCredentialStatus('openai', 'invalid', error instanceof Error ? error.message : 'Connection failed', {});
    }
  };

  const handleOpenAISave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const ok = await integrationSettings.set('openai', {
        openai_api_key: openaiApiKey,
        openai_model: openaiModel,
      });
      if (!ok) throw new Error('Failed to persist settings');
      await handleOpenAITest();
      setMessage({ type: 'success', text: 'OpenAI settings saved successfully!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save OpenAI settings' });
    } finally {
      setSaving(false);
    }
  };

  // Access checks
  const isAdminOrSuper = useMemo(() => user?.email === 'brian.kandler@gmail.com', [user?.email]); // Simplified admin check
  const tabs = useMemo(() => [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'appearance', label: 'Appearance', icon: Palette },
    ...(isAdminOrSuper ? [{ id: 'integrations', label: 'Integrations', icon: ExternalLink }] : []),
  ], [isAdminOrSuper]);

  // Support deep-linking to a specific tab via ?tab=integrations or #integrations
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const fromQuery = params.get('tab');
    const fromHash = location.hash ? location.hash.replace('#', '') : '';
    const desired = (fromQuery || fromHash || '').toLowerCase();
    const allowed = ['profile', 'appearance', ...(isAdminOrSuper ? ['integrations'] : [])];
    if (desired && allowed.includes(desired) && desired !== activeTab) {
      setActiveTab(desired);
    }
  }, [location, user?.email, activeTab]); // Removed isAdminOrSuper, added user?.email and activeTab


  // Helper component for status indicators
  const StatusIndicator: React.FC<{ 
    status: 'unconfigured' | 'valid' | 'invalid' | 'testing'; 
    lastTest?: Date | null;
    error?: string | null;
    onHelp?: () => void;
  }> = ({ status, lastTest, error, onHelp }) => {
    const getStatusConfig = () => {
      switch (status) {
        case 'valid':
          return {
            color: 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300 border-green-200 dark:border-green-800',
            icon: CheckCircle,
            text: '✓ Connected'
          };
        case 'invalid':
          return {
            color: 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300 border-red-200 dark:border-red-800',
            icon: AlertTriangle,
            text: '✗ Invalid'
          };
        case 'testing':
          return {
            color: 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-800',
            icon: Loader2,
            text: '○ Testing...'
          };
        default:
          return {
            color: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-600',
            icon: WifiOff,
            text: '○ Not configured'
          };
      }
    };

    const config = getStatusConfig();
    const Icon = config.icon;

    return (
      <div className="flex flex-col items-end">
        <div className={`px-2 py-1 rounded-full text-xs font-medium border ${config.color} flex items-center`}>
          <Icon className={`h-3 w-3 mr-1 ${status === 'testing' ? 'animate-spin' : ''}`} />
          {config.text}
        </div>
        {lastTest && status === 'valid' && (
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Last tested: {lastTest.toLocaleTimeString()}
          </div>
        )}
        {error && status === 'invalid' && (
          <div className="text-xs text-red-500 dark:text-red-400 mt-1 max-w-48 text-right">
            {error}
          </div>
        )}
        {status === 'unconfigured' && onHelp && (
          <button
            type="button"
            onClick={onHelp}
            className="text-[11px] text-blue-600 dark:text-blue-400 mt-1 hover:underline"
          >
            How to set up
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-full">
      <div className="max-w-7xl mx-auto space-y-6">
        <div>
          <div className="mb-2 flex items-center space-x-3">
            <SettingsIcon className="h-8 w-8 text-blue-600 dark:text-blue-400" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Settings</h1>
            </div>
          </div>
        </div>

      {/* Message */}
      {message && (
        <div className={`mb-6 rounded-md p-4 ${
          message.type === 'success' 
            ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' 
            : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
        }`}>
          <div className="flex">
            <div className="flex-shrink-0">
              {message.type === 'success' ? (
                <CheckCircle className="h-5 w-5 text-green-400 dark:text-green-300" />
              ) : (
                <AlertCircle className="h-5 w-5 text-red-400 dark:text-red-300" />
              )}
            </div>
            <div className="ml-3">
              <p className={`text-sm ${
                message.type === 'success' 
                  ? 'text-green-800 dark:text-green-200' 
                  : 'text-red-800 dark:text-red-200'
              }`}>
                {message.text}
              </p>
            </div>
          </div>
        </div>
      )}

        <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg overflow-hidden">
        {/* Tab Navigation */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Profile Information</h3>
                
                {/* Account Level Display */}
                <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Crown className="h-5 w-5 text-yellow-500" />
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 dark:text-white">Account Level</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {isProxyActive ? (
                            <span className="text-blue-600 dark:text-blue-400">
                              {effectiveRole} (Proxy Mode)
                            </span>
                          ) : (
                            roles && roles.length > 0 
                              ? roles.filter(role => role?.name).map(role => role.name).join(', ') 
                              : 'User'
                          )}
                        </p>
                      </div>
                    </div>
                    {hasRole('super') && (
                      <div className="text-xs text-yellow-600 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900/20 px-2 py-1 rounded-full">
                        Super Admin
                      </div>
                    )}
                  </div>
                </div>

                {/* Super Admin Role Proxy Selection */}
                {availableRoles.length > 0 && (
                  <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <div className="flex items-center space-x-3 mb-3">
                      <Users className="h-5 w-5 text-blue-500" />
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white">Role Proxy (Super Admin)</h4>
                    </div>
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        View as Role Type
                      </label>
                      <select
                        value={proxyRole || ''}
                        onChange={(e) => setProxyRole(e.target.value || null)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      >
                        <option value="">Current Role (No Proxy)</option>
                        {availableRoles.map((role) => (
                          <option key={role.id} value={role.id}>
                            {role.name.charAt(0).toUpperCase() + role.name.slice(1)} - {role.description}
                          </option>
                        ))}
                      </select>
                      {isProxyActive && (
                        <div className="mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
                          <p className="text-xs text-yellow-800 dark:text-yellow-200">
                            <strong>Proxy Active:</strong> You are viewing the application as a <strong>{effectiveRole}</strong> user. 
                            This changes what you can see and access throughout the application.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    label="First Name"
                    value={firstName}
                    onChange={setFirstName}
                    placeholder="Enter your first name"
                    validate={validateName}
                    required
                  />
                  <FormField
                    label="Last Name"
                    value={lastName}
                    onChange={setLastName}
                    placeholder="Enter your last name"
                    validate={validateName}
                    required
                  />
                </div>
                <div className="mt-4">
                  <FormField
                    label="Email"
                    value={user?.email || ''}
                    onChange={() => {}} // Read-only
                    type="email"
                    placeholder="Email address (read-only)"
                  />
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Contact support to change your email address
                  </p>
                </div>
                <div className="mt-6">
                  <button
                    onClick={handleProfileUpdate}
                    disabled={saving || profileLoading}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}


          {/* Integrations Tab */}
          {activeTab === 'integrations' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Platform Integrations</h3>
                
                <div className="space-y-8">
                  {/* OpenAI Integration */}
                  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center">
                        <a
                          href="https://platform.openai.com/api-keys"
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Open OpenAI API Keys"
                          className="h-8 w-8 bg-amber-500 rounded-lg flex items-center justify-center mr-3 hover:opacity-90"
                          title="Open OpenAI API Keys"
                        >
                          <ExternalLink className="h-4 w-4 text-white" />
                        </a>
                        <div>
                          <h4 className="text-lg font-medium text-gray-900 dark:text-white">OpenAI</h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Used for AI narrative generation</p>
                        </div>
                      </div>
                      <StatusIndicator 
                        status={openaiStatus}
                        lastTest={lastOpenaiTest}
                        error={openaiTestError}
                        onHelp={() => setHelpModal({ open: true, provider: 'openai' })}
                      />
                    </div>

                    <div className="space-y-4">
                      <FormField
                        label="API Key"
                        value={openaiApiKey}
                        onChange={setOpenaiApiKey}
                        type="password"
                        placeholder="Enter your OpenAI API key (starts with sk-)"
                        showToggle={true}
                        showPassword={showOpenaiKey}
                        onTogglePassword={() => setShowOpenaiKey(!showOpenaiKey)}
                      />
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Default Model
                          </label>
                          <button
                            type="button"
                            onClick={() => fetchOpenAIModels(openaiApiKey)}
                            disabled={!openaiApiKey.trim() || loadingModels}
                            className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                          >
                            <RefreshCw className={`h-3 w-3 ${loadingModels ? 'animate-spin' : ''}`} />
                            {loadingModels ? 'Loading...' : 'Refresh'}
                          </button>
                        </div>
                        <select
                          value={openaiModels.length ? openaiModel : ''}
                          onChange={(e) => setOpenaiModel(e.target.value)}
                          disabled={loadingModels || openaiModels.length === 0}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {openaiModels.length === 0 ? (
                            <option value="">
                              {loadingModels ? 'Loading models…' : 'Enter a valid API key to load models'}
                            </option>
                          ) : (
                            openaiModels.map((model) => (
                              <option key={model.value} value={model.value}>
                                {model.label}
                              </option>
                            ))
                          )}
                        </select>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {openaiModels.length === 0
                            ? 'Enter a valid API key, then click Refresh to load available chat models'
                            : (loadingModels ? 'Loading available models from OpenAI...' : 'Choose the OpenAI model to use for AI features')}
                        </p>
                      </div>
                      <div className="flex space-x-3">
                        <button
                          onClick={handleOpenAITest}
                          disabled={!openaiApiKey.trim() || openaiStatus === 'testing'}
                          className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {openaiStatus === 'testing' ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Testing...
                            </>
                          ) : (
                            <>
                              <RefreshCw className="h-4 w-4 mr-2" />
                              Test Connection
                            </>
                          )}
                        </button>
                        <button
                          onClick={handleOpenAISave}
                          disabled={!openaiApiKey.trim() || openaiStatus === 'testing'}
                          className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-amber-600 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Save className="h-4 w-4 mr-2" />
                          Save
                        </button>
                      </div>
                    </div>
                  </div>
                  {/* Spotify Integration */}
                  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center">
                        <a
                          href="https://developer.spotify.com/dashboard"
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Open Spotify Developer Dashboard"
                          className="h-8 w-8 bg-green-500 rounded-lg flex items-center justify-center mr-3 hover:opacity-90"
                          title="Open Spotify Developer Dashboard"
                        >
                          <ExternalLink className="h-4 w-4 text-white" />
                        </a>
                        <div>
                          <h4 className="text-lg font-medium text-gray-900 dark:text-white">Spotify Web API</h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Connect to get meta data (not Spotify for Artists)</p>
                        </div>
                      </div>
                      <StatusIndicator 
                        status={spotifyStatus}
                        lastTest={lastSpotifyTest}
                        error={spotifyTestError}
                        onHelp={() => setHelpModal({ open: true, provider: 'spotify' })}
                      />
                    </div>
                    
                    <div className="space-y-4">
                      <FormField
                        label="Client ID"
                        value={spotifyClientId}
                        onChange={setSpotifyClientId}
                        placeholder="Enter your Spotify Client ID"
                      />
                      
                      <FormField
                        label="Client Secret"
                        value={spotifyClientSecret}
                        onChange={setSpotifyClientSecret}
                        type="password"
                        placeholder="Enter your Spotify Client Secret"
                        showToggle={true}
                        showPassword={showSpotifySecret}
                        onTogglePassword={() => setShowSpotifySecret(!showSpotifySecret)}
                      />
                      
                      <div className="flex space-x-3">
                        <button
                          onClick={handleSpotifyTest}
                          disabled={!spotifyClientId.trim() || !spotifyClientSecret.trim() || spotifyStatus === 'testing'}
                          className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {spotifyStatus === 'testing' ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Testing...
                            </>
                          ) : (
                            <>
                              <RefreshCw className="h-4 w-4 mr-2" />
                              Test Connection
                            </>
                          )}
                        </button>
                        
                        <button
                          onClick={handleSpotifySave}
                          disabled={!spotifyClientId.trim() || !spotifyClientSecret.trim() || spotifyStatus === 'testing'}
                          className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Save className="h-4 w-4 mr-2" />
                          Save
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Meta Ads Integration */}
                  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center">
                        <a
                          href="https://developers.facebook.com/apps/"
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Open Meta for Developers Apps"
                          className="h-8 w-8 bg-blue-500 rounded-lg flex items-center justify-center mr-3 hover:opacity-90"
                          title="Open Meta for Developers"
                        >
                          <ExternalLink className="h-4 w-4 text-white" />
                        </a>
                        <div>
                          <h4 className="text-lg font-medium text-gray-900 dark:text-white">Meta Ads Manager</h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Connect to access advertising analytics</p>
                        </div>
                      </div>
                      <StatusIndicator 
                        status={metaStatus}
                        lastTest={lastMetaTest}
                        error={metaTestError}
                        onHelp={() => setHelpModal({ open: true, provider: 'meta' })}
                      />
                    </div>
                    
                    <div className="space-y-4">
                      <FormField
                        label="App ID"
                        value={metaAppId}
                        onChange={setMetaAppId}
                        placeholder="Enter your Meta App ID"
                      />
                      
                      <FormField
                        label="App Secret"
                        value={metaAppSecret}
                        onChange={setMetaAppSecret}
                        type="password"
                        placeholder="Enter your Meta App Secret"
                        showToggle={true}
                        showPassword={showMetaSecret}
                        onTogglePassword={() => setShowMetaSecret(!showMetaSecret)}
                      />
                      
                      <FormField
                        label="Access Token"
                        value={metaAccessToken}
                        onChange={setMetaAccessToken}
                        type="password"
                        placeholder="Enter your Meta Access Token"
                        showToggle={true}
                        showPassword={showMetaSecret}
                        onTogglePassword={() => setShowMetaSecret(!showMetaSecret)}
                      />
                      
                      <FormField
                        label="Ad Account ID"
                        value={metaAccountId}
                        onChange={setMetaAccountId}
                        placeholder="Enter your Meta Ad Account ID (e.g., act_123456789)"
                      />
                      
                      <div className="flex space-x-3">
                        <button
                          onClick={handleMetaTest}
                          disabled={!metaAccessToken.trim() || !metaAccountId.trim() || metaStatus === 'testing'}
                          className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {metaStatus === 'testing' ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Testing...
                            </>
                          ) : (
                            <>
                              <RefreshCw className="h-4 w-4 mr-2" />
                              Test Connection
                            </>
                          )}
                        </button>
                        
                        <button
                          onClick={handleMetaSave}
                          disabled={!metaAccessToken.trim() || !metaAccountId.trim() || metaStatus === 'testing'}
                          className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Save className="h-4 w-4 mr-2" />
                          Save
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Historical Data Import */}
                  <HistoricalImport />

                  {/* Sync Status & Budget Monitoring */}
                  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center">
                        <div className="h-8 w-8 bg-purple-500 rounded-lg flex items-center justify-center mr-3">
                          <RefreshCw className="h-4 w-4 text-white" />
                        </div>
                        <div>
                          <h4 className="text-lg font-medium text-gray-900 dark:text-white">Auto-Sync & Monitoring</h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Automated data sync and budget monitoring</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      {/* Sync Status */}
                      <div>
                        <h5 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Sync Status</h5>
                        <div id="sync-status-container">
                          {/* SyncStatusDisplay component will be dynamically loaded here */}
                        </div>
                      </div>
                      
                      {/* Budget Alerts */}
                      <div>
                        <h5 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Budget Alerts</h5>
                        <div id="budget-alerts-container">
                          {/* BudgetAlerts component will be dynamically loaded here */}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}


          {/* Appearance Tab */}
          {activeTab === 'appearance' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Theme</h3>
                <div className="space-y-4">
                  <div className="flex items-center space-x-4">
                    {['light', 'dark', 'system'].map((themeOption) => (
                      <label key={themeOption} className="flex items-center">
                        <input
                          type="radio"
                          name="theme"
                          value={themeOption}
                          checked={theme === themeOption}
                          onChange={(e) => setTheme(e.target.value as 'light' | 'dark' | 'system')}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300 capitalize">
                          {themeOption}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        </div>

      {/* Provider setup help modal */}
      {helpModal.open && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40" onClick={() => setHelpModal({ open: false })} />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="w-full max-w-lg bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700">
              <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {helpModal.provider === 'spotify' && 'Set up Spotify API'}
                  {helpModal.provider === 'meta' && 'Set up Meta Marketing API'}
                  {helpModal.provider === 'openai' && 'Set up OpenAI API'}
                </h3>
                <button
                  className="px-2 py-1 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
                  onClick={() => setHelpModal({ open: false })}
                >
                  ✕
                </button>
              </div>
              <div className="px-5 py-4 space-y-3 text-sm text-gray-700 dark:text-gray-300">
                {helpModal.provider === 'openai' && (
                  <div className="space-y-2">
                    <ol className="list-decimal ml-5 space-y-1">
                      <li>
                        Visit
                        <a className="text-blue-600 dark:text-blue-400 hover:underline ml-1" href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer">OpenAI API Keys</a>
                        and create a new secret key.
                      </li>
                      <li>Copy the key (starts with sk-) and paste it in the OpenAI section.</li>
                      <li>Choose a default model (e.g., gpt-4o-mini) and click Save.</li>
                      <li>Keep your key private. For production, store it securely (server-side or secret manager).</li>
                    </ol>
                  </div>
                )}
                {helpModal.provider === 'spotify' && (
                  <div className="space-y-2">
                    <ol className="list-decimal ml-5 space-y-1">
                      <li>
                        Open the
                        <a className="text-blue-600 dark:text-blue-400 hover:underline ml-1" href="https://developer.spotify.com/dashboard" target="_blank" rel="noopener noreferrer">Spotify Developer Dashboard</a>
                        and sign in.
                      </li>
                      <li>Create an App to obtain a Client ID and Client Secret.</li>
                      <li>Paste both values in the Spotify section and click Save then Test Connection.</li>
                      <li>For advanced flows, add redirect URIs in the app settings as needed.</li>
                    </ol>
                  </div>
                )}
                {helpModal.provider === 'meta' && (
                  <div className="space-y-2">
                    <ol className="list-decimal ml-5 space-y-1">
                      <li>
                        Create a Business app:
                        <a className="text-blue-600 dark:text-blue-400 hover:underline ml-1" href="https://developers.facebook.com/apps/create/" target="_blank" rel="noopener noreferrer">Create App</a>
                        (choose Business).
                      </li>
                      <li>
                        Find App ID/Secret on the app’s Basic settings page:
                        <a className="text-blue-600 dark:text-blue-400 hover:underline ml-1" href="https://developers.facebook.com/apps/" target="_blank" rel="noopener noreferrer">Apps list</a>
                        → your app → Settings → Basic.
                      </li>
                      <li>
                        Add the Marketing API product:
                        open your app →
                        <a className="text-blue-600 dark:text-blue-400 hover:underline ml-1" href="https://developers.facebook.com/apps/" target="_blank" rel="noopener noreferrer">Add Product</a>
                        → Marketing API.
                      </li>
                      <li>
                        Recommended: create a Business System User token with ads_read access:
                        <a className="text-blue-600 dark:text-blue-400 hover:underline ml-1" href="https://business.facebook.com/settings/system-users" target="_blank" rel="noopener noreferrer">Business Settings → System Users</a>,
                        assign your ad account at
                        <a className="text-blue-600 dark:text-blue-400 hover:underline ml-1" href="https://business.facebook.com/settings/ad-accounts" target="_blank" rel="noopener noreferrer">Ad Accounts</a>, then Generate Token.
                      </li>
                      <li>
                        Quick test option: use Graph API Explorer to get a user token:
                        <a className="text-blue-600 dark:text-blue-400 hover:underline ml-1" href="https://developers.facebook.com/tools/explorer/" target="_blank" rel="noopener noreferrer">Graph API Explorer</a>
                        (scope: ads_read). Exchange to long‑lived at
                        <a className="text-blue-600 dark:text-blue-400 hover:underline ml-1" href="https://developers.facebook.com/tools/accesstoken/" target="_blank" rel="noopener noreferrer">Access Token Tool</a>.
                      </li>
                      <li>
                        Paste App ID, App Secret, Access Token and Ad Account ID here, click Save then Test Connection.
                        Ad Account ID is the number (with or without the <code>act_</code> prefix).
                      </li>
                    </ol>
                  </div>
                )}
              </div>
              <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-800 flex justify-end">
                <button
                  onClick={() => setHelpModal({ open: false })}
                  className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};
